import {
    PDFArray,
    PDFDict,
    PDFName,
    PDFRawStream,
    PDFStream,
    PDFTextField,
    componentsToColor,
    decodePDFRawStream,
    defaultTextFieldAppearanceProvider,
    drawTextField,
    reduceRotation,
    rgb,
} from "pdf-lib";
import { getFieldPageAndRect } from "./pdfFieldGeometry";
import { collectPageRules, detectRuledLayout } from "./pdfRuledLines";
import { getWidgetBorderWidth, getWrapWidth, layoutFieldLines, parseDefaultAppearance } from "./pdfTextFitting";

/**
 * Ruled multiline fields.
 *
 * USCIS forms are hybrid: an AcroForm plus an XFA copy of the same form. Their
 * large text boxes are drawn over printed rules (18pt apart on the I-918 Supp. B),
 * and the line spacing that keeps the text on those rules only exists in the XFA
 * template - `<para lineHeight="18pt"/>`. AcroForm's /DA cannot express a leading,
 * so every viewer that ignores XFA picks its own: pdf-lib uses 1.2 x the font
 * height (11.1pt for 10pt Helvetica), Chrome the font's ascent - descent
 * (19.31pt for the form's Courier New). Either way the text drifts off the rules
 * a little more on every line. Only Acrobat, which reads the XFA, gets it right.
 *
 * So we work that spacing out ourselves and apply it in both places that draw
 * these fields: the textarea overlay and the appearance pdf-lib generates on
 * flatten. It comes from the XFA when the PDF still has it, and otherwise from
 * the rules printed on the page (see pdfRuledLines) - the PDFs we store are
 * unlocked first, and some tools (iLovePDF) drop the XFA on the way.
 */

// XFA measurements default to inches when they carry no unit.
const POINTS_PER_UNIT = { pt: 1, in: 72, mm: 72 / 25.4, cm: 72 / 2.54, mp: 0.001 };

const toPoints = (value) => {
    const match = String(value ?? "").trim().match(/^(-?\d*\.?\d+)\s*([a-z]*)$/i);
    if (!match) return 0;
    return parseFloat(match[1]) * (POINTS_PER_UNIT[match[2].toLowerCase() || "in"] ?? 0);
};

const childrenNamed = (element, name) =>
    Array.from(element.childNodes).filter((child) => child.nodeType === 1 && child.localName === name);
const firstChild = (element, name) => childrenNamed(element, name)[0];

const decodeStream = (stream) => {
    const bytes = stream instanceof PDFRawStream ? decodePDFRawStream(stream).decode() : stream.getContents();
    return new TextDecoder("utf-8").decode(bytes);
};

/**
 * The XFA template packet as XML text. /XFA is either one stream with the whole
 * XDP or an array of [name, stream] pairs, one per packet.
 */
const readXfaTemplate = (pdfDoc) => {
    const acroForm = pdfDoc.catalog.lookupMaybe(PDFName.of("AcroForm"), PDFDict);
    const xfa = acroForm?.lookup(PDFName.of("XFA"));

    if (xfa instanceof PDFStream) return decodeStream(xfa);
    if (!(xfa instanceof PDFArray)) return null;

    for (let idx = 0; idx + 1 < xfa.size(); idx += 2) {
        const name = xfa.lookup(idx);
        if (typeof name?.decodeText === "function" && name.decodeText() === "template") {
            const stream = xfa.lookup(idx + 1);
            return stream instanceof PDFStream ? decodeStream(stream) : null;
        }
    }
    return null;
};

/** Field-level text layout of an XFA multiline field, in points. */
const readFieldLayout = (fieldElement) => {
    const textEdit = firstChild(firstChild(fieldElement, "ui") ?? fieldElement, "textEdit");
    if (textEdit?.getAttribute("multiLine") !== "1") return null;

    // Direct children only: <caption> carries its own <para> and <font> for the label.
    const para = firstChild(fieldElement, "para");
    const lineHeight = toPoints(para?.getAttribute("lineHeight"));
    if (!(lineHeight > 0)) return null;

    const margin = firstChild(fieldElement, "margin");
    const inset = (name) => toPoints(margin?.getAttribute(name));

    return {
        lineHeight,
        marginLeft: toPoints(para.getAttribute("marginLeft")),
        marginRight: toPoints(para.getAttribute("marginRight")),
        topInset: inset("topInset"),
        bottomInset: inset("bottomInset"),
        leftInset: inset("leftInset"),
        rightInset: inset("rightInset"),
    };
};

const sameLayout = (a, b) => Object.keys(a).every((key) => Math.abs(a[key] - b[key]) < 0.01);

/**
 * Reads the line spacing of every multiline field that declares one in the XFA
 * template, keyed by the field's XFA name.
 *
 * MUST run before `pdfDoc.getForm()`: pdf-lib deletes the /XFA entry the first
 * time the form is accessed.
 *
 * A name shared by fields with different layouts maps to `null`: matching by
 * name could then pick the wrong one, and the default layout is a safer guess.
 *
 * @returns {Map<string, object|null>} empty when the PDF has no usable XFA.
 */
export const readXfaTextLayouts = (pdfDoc) => {
    const layouts = new Map();

    try {
        const xml = readXfaTemplate(pdfDoc);
        if (!xml) return layouts;

        const template = new DOMParser().parseFromString(xml, "application/xml");
        Array.from(template.getElementsByTagNameNS("*", "field")).forEach((fieldElement) => {
            const name = fieldElement.getAttribute("name");
            const layout = readFieldLayout(fieldElement);
            if (!name || !layout) return;

            if (!layouts.has(name)) layouts.set(name, layout);
            else if (layouts.get(name) && !sameLayout(layouts.get(name), layout)) layouts.set(name, null);
        });
    } catch (error) {
        // A broken XFA packet only costs us the ruled layout, never the form.
        console.warn("Could not read the XFA template:", error.message);
    }

    return layouts;
};

/**
 * The XFA layout of an AcroForm field. AcroForm names are the XFA SOM path
 * ("form1[0].#subform[1].Part3_Line6_Explanation[0]"), so its last step without
 * the index is the XFA field name.
 */
export const getXfaTextLayout = (layouts, field) => {
    if (!layouts?.size || !field.isMultiline()) return null;
    const name = field.getName().split(".").pop().replace(/\[\d+\]$/, "");
    return layouts.get(name) ?? null;
};

/**
 * Builds the lookup of the line layout of ruled fields: the XFA's when the PDF
 * still has one for the field, otherwise the one implied by the rules printed
 * under it, otherwise null (an ordinary box, laid out by pdf-lib as before).
 *
 * MUST be created before `pdfDoc.getForm()`, which deletes the XFA. Each page's
 * drawing is read at most once, and only for pages with a multiline field.
 *
 * @returns {(field: PDFField) => object|null}
 */
export const createRuledLayoutResolver = (pdfDoc) => {
    const xfaLayouts = readXfaTextLayouts(pdfDoc);
    const pages = pdfDoc.getPages();
    const pageRules = new Map();

    return (field) => {
        if (!(field instanceof PDFTextField) || !field.isMultiline()) return null;

        const xfaLayout = getXfaTextLayout(xfaLayouts, field);
        if (xfaLayout) return xfaLayout;

        const widgetInfo = getFieldPageAndRect(field, pages);
        if (!widgetInfo || widgetInfo.pageIndex < 0) return null;

        const { pageIndex, rect } = widgetInfo;
        if (!pageRules.has(pageIndex)) pageRules.set(pageIndex, collectPageRules(pages[pageIndex]));

        return detectRuledLayout(pageRules.get(pageIndex), rect, parseDefaultAppearance(field).fontSize);
    };
};

/**
 * Where the text of a ruled field goes, in points from the widget's top-left:
 * the left edge of the text, the width lines are measured against (before
 * `getWrapWidth`'s gutter), and how many lines of `lineHeight` fit.
 */
export const getRuledTextBox = (layout, rect, borderWidth) => {
    const left = borderWidth + layout.leftInset + layout.marginLeft;
    const right = borderWidth + layout.rightInset + layout.marginRight;
    const top = borderWidth + layout.topInset;
    const usableHeight = rect.height - top - borderWidth - layout.bottomInset;

    return {
        x: left,
        top,
        width: rect.width - left - right,
        lineHeight: layout.lineHeight,
        // The tolerance absorbs /Rect rounding: 215.999pt still holds 12 lines of 18pt.
        maxLines: Math.max(1, Math.floor(usableHeight / layout.lineHeight + 0.01)),
    };
};

/**
 * Distance from the bottom of a line box to its baseline. XFA puts the extra
 * leading above the text, so the baseline sits one descent above the line's
 * bottom - which is where the printed rule is.
 */
const getDescent = (font, fontSize) => font.heightAtSize(fontSize) - font.heightAtSize(fontSize, { descender: false });

/**
 * An appearance provider for `field.updateAppearances()` that draws the text at
 * the ruled field's line spacing. Line breaks are computed with the same
 * `layoutFieldLines` and wrap width as `splitTextForAddendum`, so the lines drawn
 * are exactly the lines that were counted.
 *
 * The text is drawn in black: pdf-lib does not export its /DA color parser, and
 * every ruled field seen so far declares "0 g".
 */
export const ruledTextAppearanceProvider = (box, fontSize) => (textField, widget, font) => {
    const ap = widget.getAppearanceCharacteristics();
    // These fields are never rotated on the forms seen so far; a rotated one keeps pdf-lib's layout.
    if (reduceRotation(ap?.getRotation()) !== 0) return defaultTextFieldAppearanceProvider(textField, widget, font);

    const { width, height } = widget.getRectangle();
    const borderWidth = getWidgetBorderWidth(widget);
    const descent = getDescent(font, fontSize);
    const wrapWidth = getWrapWidth(box.width, fontSize);

    const textLines = layoutFieldLines(textField.getText() ?? "", font, fontSize, wrapWidth).map((line, index) => ({
        text: line.text,
        encoded: font.encodeText(line.text),
        x: box.x,
        y: Math.abs(height) - box.top - (index + 1) * box.lineHeight + descent,
        width: font.widthOfTextAtSize(line.text, fontSize),
        height: font.heightAtSize(fontSize),
    }));

    return drawTextField({
        x: borderWidth / 2,
        y: borderWidth / 2,
        width: Math.abs(width) - borderWidth,
        height: Math.abs(height) - borderWidth,
        borderWidth,
        borderColor: componentsToColor(ap?.getBorderColor()),
        color: componentsToColor(ap?.getBackgroundColor()),
        textColor: rgb(0, 0, 0),
        font: font.name,
        fontSize,
        textLines,
        padding: 1,
    });
};

/**
 * Vertical metrics of Arial (hhea ascender/descender per em). The overlay uses
 * Arial because it has Helvetica's widths, so the textarea wraps where pdf-lib
 * will, and its metrics are the same on every OS that has it.
 */
const ARIAL_ASCENT = 0.905;
const ARIAL_DESCENT = 0.212;
// Helvetica's descender (207/1000), what `getDescent` yields for the printed text.
const PRINT_DESCENT = 0.207;

/**
 * The overlay draws its own rules (see `getRuledTextareaStyle`). The fill is the
 * opaque equivalent of the overlay's rgba(200, 200, 255, 0.3) over the page, so
 * the field looks as before; the rules are toned down so the text stands out.
 */
const RULED_BACKGROUND = "#efefff";
const RULE_COLOR = "rgba(0, 0, 0, 0.4)";
// USCIS rules are 0.72pt strokes.
const RULE_THICKNESS = 0.72;

/**
 * Textarea styles that put each line of the overlay on its printed rule, for a
 * page drawn at `scale` px per point. The textarea's 1px border sits inside its
 * box (border-box), so it comes out of the padding.
 *
 * CSS centers the font's content area in each line box; the padding moves that
 * baseline down to where the PDF draws it, one descent above the rule.
 *
 * The page's rules are part of the page image and stay put while the textarea
 * scrolls, so its text would slide across them. Instead the field is opaque and
 * draws its own rules as a background that scrolls with the text
 * (`background-attachment: local`); unscrolled, they sit exactly on the printed
 * ones. One gradient tile per line, its rule at the tile's bottom edge.
 */
export const getRuledTextareaStyle = (box, rect, fontSize, scale) => {
    const lineBoxBaseline = (box.lineHeight - (ARIAL_ASCENT + ARIAL_DESCENT) * fontSize) / 2 + ARIAL_ASCENT * fontSize;
    const printBaseline = box.lineHeight - PRINT_DESCENT * fontSize;
    const wrapWidth = getWrapWidth(box.width, fontSize);

    const lineHeightPx = box.lineHeight * scale;
    const ruleThicknessPx = Math.max(1, RULE_THICKNESS * scale);
    const ruleStart = lineHeightPx - ruleThicknessPx;
    // The background starts inside the 1px border; centering the stripe on the
    // rule takes half its thickness back.
    const tileOffset = box.top * scale - 1 + ruleThicknessPx / 2;

    return {
        backgroundColor: RULED_BACKGROUND,
        backgroundImage: `linear-gradient(to bottom, transparent ${ruleStart}px, ${RULE_COLOR} ${ruleStart}px, ${RULE_COLOR} ${lineHeightPx}px)`,
        backgroundSize: `100% ${lineHeightPx}px`,
        backgroundPosition: `0 ${tileOffset}px`,
        backgroundRepeat: "repeat-y",
        backgroundAttachment: "local",
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: `${fontSize * scale}px`,
        // The app's global `* { letter-spacing: 0.05rem }` widens the text and wraps it before pdf-lib would.
        letterSpacing: "normal",
        lineHeight: `${box.lineHeight * scale}px`,
        paddingTop: `${Math.max(0, (box.top + printBaseline - lineBoxBaseline) * scale - 1)}px`,
        paddingBottom: 0,
        paddingLeft: `${Math.max(0, box.x * scale - 1)}px`,
        paddingRight: `${Math.max(0, (rect.width - box.x - wrapWidth) * scale - 1)}px`,
    };
};
